import * as stylex from "@stylexjs/stylex";
import { Check, X } from "lucide-react";
import { useCallback, useId, useLayoutEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode, SyntheticEvent } from "react";
import Button from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import Popover from "@/components/ui/Popover";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, shadows, space, text } from "@/design/tokens.stylex";

// The reporting row's chip panels (Date and Filter) share one shape: one
// column of small labelled sections and Cancel / Apply in a footer across it. Edits are staged in the panel and only reach the
// report on Apply; Escape, a press outside or Cancel discards them.

const styles = {
  panel: {
    kmVPX3: "x1717udv",
    kORKVm: "x1odjw0f",
    $$css: true
  },
  option: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x166cprz",
    kzqmXN: "xh8yej3",
    kAzted: "x1kjopne",
    kg3NbH: "x1wa2ap4",
    kaIpWk: "x1wpy2cq",
    kWkggS: "xjbqb8w x1fns82p x15twbwe",
    kMwMTN: "x1e3avs9",
    kGuDYH: "xtu3avg",
    k9WMMc: "xdpxx8g",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  selected: {
    kMwMTN: "xgwkh9f",
    k63SB2: "xk50ysn",
    $$css: true
  },
  optionLabel: {
    kUk6DE: "x98rzlu",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  segment: {
    kZKoxP: "x5yr21d",
    kg3NbH: "xtft4pz",
    kaIpWk: "x3kktt9",
    kGuDYH: "x7x6yu3",
    k63SB2: "xk50ysn",
    khDVqt: "xuxw1ft",
    kMwMTN: "x1i6qc9y xfpavck",
    $$css: true
  },
  segmentOn: {
    kWkggS: "x1h1f4i9",
    kGVxlE: "x17ljfxq",
    kMwMTN: "xgwkh9f xfpavck",
    $$css: true
  },
  chip: {
    kOIVth: "x166cprz",
    $$css: true
  },
  foot: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x166cprz",
    k8WAf4: "x6zw8kb",
    kg3NbH: "xtft4pz",
    kVAEAm: "x7wzq59",
    krVfgx: "x1ey2m1c",
    kWkggS: "x1h1f4i9",
    $$css: true
  },
  joined: {
    kfmiAY: "x1ga7v0g",
    kT0f0o: "x16uus16",
    ke9TFa: "x10w94by",
    $$css: true
  },
  clear: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "xgd8bvy",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "xzbo3x7",
    kfmiAY: "xmwqe6e",
    kT0f0o: "xnydjy2",
    kWkggS: "x1h1f4i9 x1fns82p",
    kMwMTN: "x1i6qc9y xfpavck",
    $$css: true
  }
};
interface PanelOption {
  value: string;
  label: string;
}

/**
 * A chip that opens a panel of staged settings. The owner keeps `open` and the
 * staged draft; the panel asks to close on Escape or a press outside, and Tab
 * past either end continues from the chip.
 */
export function ChipPanel({
  ariaLabel,
  panelLabel,
  icon,
  label,
  onClear,
  clearLabel,
  open,
  onOpenChange,
  dirty,
  onApply,
  children
}: {
  /** The chip's accessible name; it contains what the chip shows. */
  ariaLabel: string;
  panelLabel: string;
  icon?: ReactNode;
  /** What the chip reads; several parts show with quiet dots between them. */
  label: string | readonly string[];
  /** Offered while something is set: a small × beside the chip. */
  onClear?: () => void;
  clearLabel?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether anything is staged; Apply waits for a change. */
  dirty: boolean;
  onApply: () => void;
  children: ReactNode;
}) {
  const chipRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // A calendar or dropdown opened from the panel is portalled too. A press
  // inside it still reaches this panel through React, and must not close the
  // panel. (Escape needs no help: the top overlay takes it.)
  const nested = useRef(false);
  const panelId = useId();
  const close = useCallback((reason?: "outside" | "escape") => {
    if (nested.current) {
      nested.current = false;
      return;
    }
    onOpenChange(false);
    if (reason !== "outside") chipRef.current?.focus();
  }, [onOpenChange]);

  // When the panel closes from inside (Apply, Cancel), its focused button goes
  // with it; continue from the chip.
  const wasOpen = useRef(open);
  useLayoutEffect(() => {
    if (wasOpen.current && !open) {
      const active = document.activeElement;
      if (!active || active === document.body) chipRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    // Start on the first list's current choice.
    const body = bodyRef.current;
    (body?.querySelector<HTMLElement>('[role="option"][tabindex="0"]') ?? body?.querySelector<HTMLElement>("button"))?.focus({
      preventScroll: true
    });
  }, [open]);
  const markNested = (event: SyntheticEvent) => {
    if (!bodyRef.current?.contains(event.target as Node)) nested.current = true;
  };
  function keyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Tab" || !bodyRef.current?.contains(event.target as Node)) return;
    const focusable = Array.from(bodyRef.current.querySelectorAll<HTMLElement>("button:not(:disabled)")).filter(element => element.tabIndex >= 0);
    if (event.shiftKey && event.target === focusable[0]) {
      event.preventDefault();
      close();
    } else if (!event.shiftKey && event.target === focusable.at(-1)) close();
  }
  return <>
      <span className="x3nfvp2 x1qjc9v5 x193iq5w">
        <Chip ref={chipRef} icon={icon} label={typeof label === "string" ? label : <ChipSummary parts={label} />} xstyle={[styles.chip, onClear && styles.joined]} menu active={false} aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => onOpenChange(!open)} onKeyDown={event => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          onOpenChange(true);
        }
      }} />
        {onClear && <button type="button" aria-label={clearLabel} onClick={onClear} {...stylex.props(recipes.bareButton, styles.clear, recipes.focusRing)}>
            <X size={12} aria-hidden />
          </button>}
      </span>
      <Popover open={open} anchorRef={chipRef} onClose={close} id={panelId} role="dialog" aria-label={panelLabel} xstyle={styles.panel}>
        <div ref={bodyRef} onMouseDown={markNested} onKeyDown={keyDown}>
          {children}
          <div {...stylex.props(styles.foot, recipes.hairlineTopSoft)}>
            <Button variant="ghost" size="sm" onClick={() => close()}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" disabled={!dirty} onClick={onApply}>
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>;
}
function ChipSummary({
  parts
}: {
  parts: readonly string[];
}) {
  return <span className="x3nfvp2 x6s0dn4 x166cprz xeuugli">
      {parts.map((part, index) => <span key={part} className="x3nfvp2 x6s0dn4 x166cprz xeuugli">
          {index > 0 && <span aria-hidden className="x2lah0s x1g8rjiy xuoj239 x3kktt9 x1u2vnfd" />}
          <span {...stylex.props(recipes.truncate)}>{part}</span>
        </span>)}
    </span>;
}

/** The panel's one column of labelled sections. */
export function PanelColumn({
  children
}: {
  children: ReactNode;
}) {
  return <div className="x78zum5 xdt5ytf x1l51sop x1l2rt3b x1usz39j xw1tjjd">{children}</div>;
}

/** A setting in the column: a quiet label over its control. */
export function ChipPanelSection({
  label,
  labelId,
  children
}: {
  label: string;
  labelId?: string;
  children: ReactNode;
}) {
  return <div className="x78zum5 xdt5ytf x1cy8zhl x166cprz">
      <span id={labelId} className="x1i6qc9y x7x6yu3">
        {label}
      </span>
      {children}
    </div>;
}

/** A short list of choices, check on the right; arrow keys move between them. */
function PanelListbox({
  name,
  options,
  selected,
  multiple = false,
  grid = false,
  onSelect
}: {
  /** Names the list for assistive tech. */
  name: string;
  /** Two rows side by side, for a longer list of short choices. */
  grid?: boolean;
  options: readonly PanelOption[];
  selected: readonly string[];
  multiple?: boolean;
  onSelect: (value: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const tabStop = options.find(option => selected.includes(option.value)) ?? options[0];
  function keyDown(event: KeyboardEvent<HTMLDivElement>) {
    const buttons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "ArrowDown" ? (current + 1) % buttons.length : event.key === "ArrowUp" ? (current - 1 + buttons.length) % buttons.length : event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : undefined;
    if (next === undefined) return;
    event.preventDefault();
    buttons[next]?.focus();
  }
  return <div ref={listRef} role="listbox" aria-label={name} aria-multiselectable={multiple || undefined} onKeyDown={keyDown} {...{
    0: {
      className: "x78zum5 xdt5ytf xkh2ocl"
    },
    1: {
      className: "xdt5ytf xkh2ocl xrvj5dj xnby9oq xbqp58d"
    }
  }[!!grid << 0]}>
      {options.map(option => {
      const isSelected = selected.includes(option.value);
      return <button key={option.value} type="button" role="option" aria-selected={isSelected} tabIndex={option === tabStop ? 0 : -1} onClick={() => onSelect(option.value)} {...stylex.props(recipes.bareButton, styles.option, isSelected && styles.selected)}>
            <span {...stylex.props(styles.optionLabel, recipes.truncate)}>{option.label}</span>
            {isSelected && <Check aria-hidden size={13} />}
          </button>;
    })}
    </div>;
}

/** A labelled list of rows, check on the right. */
export function PanelListSection(props: Parameters<typeof PanelListbox>[0] & {
  label: string;
}) {
  const {
    label,
    ...list
  } = props;
  return <ChipPanelSection label={label}>
      <div className="xkh2ocl x5mmoh">
        <PanelListbox {...list} />
      </div>
    </ChipPanelSection>;
}

/** Compact, fully rounded segmented pills for one choice among a few. */
export function PanelSegments<T extends string>({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly {
    value: T;
    label: string;
  }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const labelId = useId();
  return <ChipPanelSection label={label} labelId={labelId}>
      <div role="group" aria-labelledby={labelId} className="x3nfvp2 x3d5knr x1f38ord xyeyesb x3kktt9 xlcwlql">
        {options.map(option => <button key={option.value} type="button" aria-pressed={option.value === value} onClick={() => onChange(option.value)} {...stylex.props(recipes.bareButton, styles.segment, recipes.focusRing, recipes.transition, option.value === value && styles.segmentOn)}>
            {option.label}
          </button>)}
      </div>
    </ChipPanelSection>;
}